import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "shared/BaseFragment";
import React, {ReactElement, useEffect, useState} from "react";
import {StyledBoxColumn, StyledBoxRow, StyledSpan} from "shared/StyledComponents";
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  accordionSummaryClasses,
  AccordionSummaryProps,
  Button,
  CircularProgress,
  styled,
  TextField,
  Typography
} from "@mui/material";
import {DIVIDER, DIVIDER_BLACK, PD_LG, PD_MD, PD_XLG2} from "shared/dimens";
import {ArrowForwardIosOutlined, CheckCircleOutlined, RemoveCircleOutlineOutlined} from "@mui/icons-material";
import {StoredDataFileUploadFragment} from "shared/StoredDateFileUploadFragment";
import {
  SHOWCASE_ITEM_TYPE_METADATAS,
  ShowcaseItem,
  ShowcaseItemDraft,
  ShowcaseItems,
  ShowcaseItemType,
  VideoDataType
} from "../types";
import {DesktopFile} from "shared/files";
import {BaseApp} from "shared/BaseApp";
import {EditShowcaseItemDraft} from "./EditShowcaseItem";
import {FileUtil} from "shared/file_util";
import {$KTS, Action, KeyTextStrings, LocalFile} from "shared/types";
import {parseBlob, selectCover} from "music-metadata";
import {storageRef} from "shared/storage";
import {v4 as uuid} from "uuid"
import {getDownloadURL, uploadBytes} from "@firebase/storage";
import {MimeTypeMap} from "shared/mime_types";
import {lightGray, mediumGray} from "../../shared/colors";
import {App} from "../App";
import Markdown from "react-markdown";
import {FullscreenContainer} from "../app-shared/common/Components";
import {PathProps} from "../../index";

export class ComposerHelper {

  static addItem(path: PathProps, listener?: ComposerFragmentListener): void {
    App.CONTEXT.showFullscreenDialog(() => <FullscreenContainer
      title={"Add New"}
      actions={[
        new Action("Cancel", () => App.CONTEXT.hideDialog()),
      ]}>
      <ComposerFragment path={path} listener={listener}/>
    </FullscreenContainer>);
  }
}

export interface ComposerFragmentListener {
  onComposerUploadDone(items: ShowcaseItem[]): boolean;
}

export type ComposerFragmentProps = BaseFragmentProps & {
  listener: ComposerFragmentListener,
}

enum UploadRenderState {
  NONE,
  PROCESS,
  EDIT,
  SAVE,
}

const UPLOAD_METHOD_KTS = new KeyTextStrings([
  $KTS(ShowcaseItemType.IMAGE, "Image"),
  $KTS(ShowcaseItemType.VIDEO, "Video"),
  $KTS(ShowcaseItemType.AUDIO, "Audio"),
  $KTS(ShowcaseItemType.SCRATCH_PROJECT, "Scratch Project"),
]);

type ComposerFragmentState = BaseFragmentState & {
  showcaseItemType: ShowcaseItemType,
  uploadRenderState: UploadRenderState,
  draft?: ShowcaseItemDraft,
}

type EmbedData = {
  id?: string,
  title: string,
  description: string,
  thumbnailUrl: string,
  html?: string,
}

function UrlView(props: {
  showcaseItemType: ShowcaseItemType,
  title: string,
  text: string,
  doValidate?: (url: string) => Promise<EmbedData | null>,
  onValidationResult?: (url: string, embedData: EmbedData) => void,
}): ReactElement {
  const [url, setUrl] = useState<string>("");
  const [validating, setValidating] = useState<boolean>(false);
  const [validationResult, setValidationResult] = useState<EmbedData | null | undefined>(undefined);
  useEffect(() => {
    setUrl("");
  }, [props.showcaseItemType]);
  useEffect(() => {
    props.onValidationResult(url, validationResult);
  }, [validationResult]);
  return <StyledBoxColumn>
    <Typography variant="h6">{props.title}</Typography>
    <Typography variant="caption">{props.text}</Typography>
    <StyledBoxRow style={{alignItems: "center"}}>
      <TextField value={url} style={{flexGrow: 1}} onChange={event => setUrl(event.target.value)}/>
      <div style={{width: 24, height: 24}}>
        {
          validationResult !== undefined
            ? (
              validationResult !== null
                ? <CheckCircleOutlined/>
                : <RemoveCircleOutlineOutlined/>
            )
            : null
        }
      </div>
    </StyledBoxRow>
    <Button disabled={!url || validating} onClick={() => {
      if (props.doValidate) {
        setValidating(true);
        props.doValidate(url)
          .then(result => setValidationResult(result))
          .finally(() => setValidating(false));
      } else {
        setValidationResult(undefined);
      }
    }}>Validate</Button>
  </StyledBoxColumn>;
}

const StyledAccordion = styled((props: AccordionProps) => (
  <Accordion disableGutters elevation={0} square {...props} />
))(({theme}) => ({
  border: DIVIDER,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const StyledAccordionSummary = styled((props: AccordionSummaryProps) => (
  <AccordionSummary
    expandIcon={<ArrowForwardIosOutlined/>}
    {...props}
  />
))(({theme}) => ({
  backgroundColor: lightGray,
  flexDirection: 'row-reverse',
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: 'rotate(90deg)',
    },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
  ...theme.applyStyles('dark', {
    backgroundColor: mediumGray,
  }),
}));

const StyledAccordionDetails = styled(AccordionDetails)(({theme}) => ({
  padding: theme.spacing(2),
  borderTop: DIVIDER,
}));

type YoutubeApiResult = {
  type: string,
  title: string,
  author_name: string,
  thumbnail_url: string,
  html: string,
}

function MediaView(props: {
  showcaseItemType: ShowcaseItemType,
  mimeTypes: string[],
  processFileUpload: (url: string, file: LocalFile) => Promise<void>,
  doValidate?: (url: string) => Promise<EmbedData | null>,
  onValidationResult?: (url: string, embedData: EmbedData | null) => void
}): ReactElement {
  const [expanded, setExpanded] = React.useState<string | false>("upload");
  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };
  const metadata = SHOWCASE_ITEM_TYPE_METADATAS.findMetadata(props.showcaseItemType);
  return <StyledBoxColumn style={{flexGrow: 1, marginTop: PD_MD}}>
    <StyledAccordion expanded={expanded === "upload"} onChange={handleChange("upload")}>
      <StyledAccordionSummary>
        <Typography variant="h6">Upload</Typography>
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        <StyledBoxColumn style={{alignItems: "center"}}>
          <metadata.iconType style={{color: metadata.color, width: 72, height: 72}}/>
          <Typography variant="h6">{metadata.uploadTitle}</Typography>
          <Typography component="div"><Markdown>{metadata.uploadText}</Markdown></Typography>
          <Button onClick={() => App.CONTEXT.showDialog(null, () => <StoredDataFileUploadFragment
            deferUpload
            mimeTypes={props.mimeTypes}
            storedDataSource={props.showcaseItemType}
            onSelectionChanged={(urls: string[], files: DesktopFile[]) => {
              props.processFileUpload(urls[0], files[0].file);
            }}/>)}>
            Upload
          </Button>
        </StyledBoxColumn>
      </StyledAccordionDetails>
    </StyledAccordion>
    {metadata.supportsEmbed
      ? <StyledAccordion expanded={expanded === "embed"} onChange={handleChange("embed")}>
        <StyledAccordionSummary>
          <Typography variant="h6">Embed</Typography>
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          <UrlView
            showcaseItemType={props.showcaseItemType}
            title={"URL"}
            text={metadata.embedText}
            doValidate={props.doValidate}
            onValidationResult={props.onValidationResult}/>
        </StyledAccordionDetails>
      </StyledAccordion>
      : null}
  </StyledBoxColumn>;
}

type ScratchApiResult = {
  id: number,
  title: string,
  description: string,
  image: string,
}

function ScratchProjectView(props: {
  onValidationResult: (url: string, embedData: EmbedData | null) => void
}): ReactElement {
  const metadata = SHOWCASE_ITEM_TYPE_METADATAS.findMetadata(ShowcaseItemType.SCRATCH_PROJECT);
  return <UrlView
    showcaseItemType={ShowcaseItemType.SCRATCH_PROJECT}
    title={metadata.embedTitle}
    text={metadata.embedText}
    doValidate={async (url: string) => {
      const regex = /https\:\/\/scratch\.mit\.edu\/projects\/(\d+)\/?.*/;
      const found: string[] = url.match(regex);
      const valid = Boolean(found);
      if (valid) {
        const projectId = found[1];
        const apiResult = await fetch(`https://corsproxy.throwaway092016.workers.dev/?url=https://api.scratch.mit.edu/projects/${projectId}`).then(res => res.json()) as ScratchApiResult;
        if (apiResult?.id?.toString() === projectId) {
          return {
            id: projectId,
            title: apiResult.title,
            description: apiResult.description,
            thumbnailUrl: apiResult.image,
          } as EmbedData;
        }
      }
      App.CONTEXT.showToast("Invalid project URL. Please try again.");
      return null;
    }}
    onValidationResult={props.onValidationResult}/>;
}

class ComposerFragment extends BaseFragment<ComposerFragmentProps, ComposerFragmentState> {

  protected onCreateState(): ComposerFragmentState {
    return {
      ...super.onCreateState(),
      showcaseItemType: ShowcaseItemType.IMAGE,
      uploadRenderState: UploadRenderState.NONE,
    };
  }

  private renderShowcaseItemTypeFile(showcaseItemType: ShowcaseItemType, mimeTypes: string[], doValidate?: (url: string) => Promise<EmbedData | null>, onValidationResult?: (url: string, embedData: EmbedData | null) => void): ReactElement {
    return <MediaView
      showcaseItemType={showcaseItemType}
      mimeTypes={mimeTypes}
      processFileUpload={(url, file) => this.processFileUpload(url, file)}
      doValidate={doValidate}
      onValidationResult={onValidationResult}
    />;
  }

  private renderShowcaseItemTypeScratchProject(): ReactElement {
    return <ScratchProjectView
      onValidationResult={(url, embedData) => {
        if (embedData) {
          const draft = ShowcaseItemDraft.createNew(ShowcaseItemType.SCRATCH_PROJECT);
          draft.scratchProjectData.projectId = embedData.id;
          draft.title = embedData.title;
          draft.description = embedData.description;
          draft.coverImageUrl = embedData.thumbnailUrl;
          this.setState({
            uploadRenderState: UploadRenderState.EDIT,
            draft: draft,
          })
        }
      }}
    />;
  }

  private renderShowcaseItemType(): ReactElement {
    let doValidate: (url: string) => Promise<EmbedData | null>;
    let onValidationResult: (url: string, embedData: EmbedData | null) => void;
    switch (this.state.showcaseItemType) {
      default:
      case ShowcaseItemType.IMAGE:
        return this.renderShowcaseItemTypeFile(ShowcaseItemType.IMAGE, [MimeTypeMap.MimeType_image_png, MimeTypeMap.MimeType_image_jpeg]);
      case ShowcaseItemType.VIDEO:
        doValidate = async (url: string) => {
          const apiResult = await fetch(`https://www.youtube.com/oembed?format=json&url=${url}`).then(res => res.json()) as YoutubeApiResult;
          if (apiResult?.type === "video") {
            return {
              title: apiResult.title,
              description: apiResult.author_name,
              thumbnailUrl: apiResult.thumbnail_url,
              html: apiResult.html,
            } as EmbedData;
          }
          return null;
        }
        onValidationResult = (url: string, embedData: EmbedData | null) => {
          if (embedData) {
            const draft = ShowcaseItemDraft.createNew(ShowcaseItemType.VIDEO);
            draft.videoData.url = url;
            draft.videoData.type = VideoDataType.EMBED_YOUTUBE;
            draft.videoData.embedHtml = embedData.html;
            draft.title = embedData.title;
            draft.description = embedData.description;
            draft.coverImageUrl = embedData.thumbnailUrl;
            this.setState({
              uploadRenderState: UploadRenderState.EDIT,
              draft: draft,
            });
          }
        };
        return this.renderShowcaseItemTypeFile(ShowcaseItemType.VIDEO, [MimeTypeMap.MimeType_video_mp4], doValidate, onValidationResult);
      case ShowcaseItemType.AUDIO:
        return this.renderShowcaseItemTypeFile(ShowcaseItemType.AUDIO, [MimeTypeMap.MimeType_audio_mpeg, MimeTypeMap.MimeType_audio_x_wav]);
      case ShowcaseItemType.SCRATCH_PROJECT:
        return this.renderShowcaseItemTypeScratchProject();
    }
  }

  private renderUploadStateNone(): ReactElement {
    return <>
      <StyledBoxRow>
        {UPLOAD_METHOD_KTS.values.map(value => <Button
          variant={this.state.showcaseItemType === value.key ? "contained" : null}
          onClick={() => this.setState({showcaseItemType: value.key as ShowcaseItemType})}>
          {value.text}
        </Button>)}
      </StyledBoxRow>
      <StyledBoxColumn style={{height: 400, justifyContent: "center"}}>
        {this.renderShowcaseItemType()}
      </StyledBoxColumn>
    </>
  }

  private async processFileUpload(url: string, file: LocalFile): Promise<void> {
    this.setState({
      uploadRenderState: UploadRenderState.PROCESS,
    });
    const draft = ShowcaseItemDraft.createNew(this.state.showcaseItemType);
    switch (draft.type) {
      case ShowcaseItemType.IMAGE:
        draft.imageData.url = url;
        break;
      case ShowcaseItemType.VIDEO:
        draft.videoData.url = url;
        break;
      case ShowcaseItemType.AUDIO:
        draft.audioData.url = url;
        break;
    }
    draft.blob = file.blob;
    const extension = FileUtil.extensionFromFileName(file.name);
    if (MimeTypeMap.isImage(MimeTypeMap.getInstance().getMimeTypeFromExtension(extension))) {
      draft.coverImageBlob = await FileUtil.resizeImage(draft.blob, 400, 400);
      draft.coverImageUrl = URL.createObjectURL(draft.coverImageBlob);
    } else if (extension === "mp3") {
      const metadata = await parseBlob(file.blob);
      draft.title = metadata?.common.title;
      draft.description = metadata?.common.longDescription || metadata?.common.description?.join("\n\n") || metadata?.common.artist;
      const pictures = metadata?.common.picture;
      if (pictures) {
        const coverData = selectCover(pictures);
        draft.coverImageBlob = await FileUtil.resizeImage(new Blob([coverData.data]), 400, 400);
        draft.coverImageUrl = URL.createObjectURL(draft.coverImageBlob);
      }
      draft.audioData.metadata = metadata;
    }
    if (!draft.title) {
      draft.title = FileUtil.nameFromFileName(file.name);
    }
    this.setState({
      uploadRenderState: UploadRenderState.EDIT,
      draft: draft,
    });
  }

  private renderUploadProgress(): ReactElement {
    return <StyledBoxColumn style={{alignItems: "center", height: 480, justifyContent: "center", flexGrow: 1}}>
      <CircularProgress/>
    </StyledBoxColumn>;
  }

  private renderUploadStateProcess(): ReactElement {
    return this.renderUploadProgress();
  }

  private renderUploadStateEdit(): ReactElement {
    return <EditShowcaseItemDraft
      showcaseItem={this.state.draft}
      setShowcaseItem={showcaseItem => this.setState({draft: showcaseItem})}/>;
  }

  private renderUploadStateSave(): ReactElement {
    return this.renderUploadProgress();
  }

  private renderUpload() {
    switch (this.state.uploadRenderState) {
      case UploadRenderState.NONE:
        return this.renderUploadStateNone();
      case UploadRenderState.PROCESS:
        return this.renderUploadStateProcess();
      case UploadRenderState.EDIT:
        if (this.state.draft) {
          return this.renderUploadStateEdit();
        }
        break;
      case UploadRenderState.SAVE:
        return this.renderUploadStateSave();
    }
    return this.renderUploadStateNone();
  }

  private async saveUpload(): Promise<void> {
    switch (this.state.showcaseItemType) {
      case ShowcaseItemType.IMAGE:
        await this.saveFileUpload();
        break;
      case ShowcaseItemType.VIDEO:
        switch (this.state.draft.videoData.type){
          default:
          case VideoDataType.URL:
            await this.saveFileUpload();
            break;
          case VideoDataType.EMBED_YOUTUBE:
            await this.saveItem();
            break;
        }
        break;
      case ShowcaseItemType.AUDIO:
        await this.saveFileUpload();
        break;
      case ShowcaseItemType.SCRATCH_PROJECT:
        await this.saveItem();
    }
  }

  private async saveFileUpload(): Promise<void> {
    this.setState({
      uploadRenderState: UploadRenderState.SAVE,
    });
    const showcaseItem = ShowcaseItem.createNew(this.state.showcaseItemType);
    Object.assign(showcaseItem, this.state.draft);
    const fileRef = storageRef("items/" + uuid());
    const url = await uploadBytes(fileRef, this.state.draft.blob)
      .then(snapshot => getDownloadURL(snapshot.ref));
    switch (showcaseItem.type) {
      case ShowcaseItemType.IMAGE:
        showcaseItem.imageData.url = url;
        break;
      case ShowcaseItemType.VIDEO:
        showcaseItem.videoData.url = url;
        break;
      case ShowcaseItemType.AUDIO:
        showcaseItem.audioData.url = url;
        break;
    }
    if (this.state.draft.coverImageBlob) {
      const coverImageFileRef = storageRef("item-covers/" + (uuid() + ".jpg"));
      showcaseItem.coverImageUrl = await uploadBytes(coverImageFileRef, this.state.draft.coverImageBlob, {contentType: MimeTypeMap.MimeType_image_jpeg})
        .then(snapshot => getDownloadURL(snapshot.ref));
    }
    await ShowcaseItems.getInstance().addListItem(showcaseItem);
    BaseApp.CONTEXT.showToast("Item added.");
    this.setState({
      uploadRenderState: UploadRenderState.NONE,
      draft: null,
    });
    if (!this.props.listener?.onComposerUploadDone([showcaseItem])) {
      App.CONTEXT.hideDialog();
    }
  }

  private async saveItem(): Promise<void> {
    this.setState({
      uploadRenderState: UploadRenderState.SAVE,
    });
    const showcaseItem = ShowcaseItem.createNew(this.state.showcaseItemType);
    Object.assign(showcaseItem, this.state.draft);
    await ShowcaseItems.getInstance().addListItem(showcaseItem);
    BaseApp.CONTEXT.showToast("Item added.");
    this.setState({
      uploadRenderState: UploadRenderState.NONE,
      draft: null,
    });
    if (!this.props.listener?.onComposerUploadDone([showcaseItem])) {
      App.CONTEXT.hideDialog();
    }
  }

  protected renderContainerContent(): React.ReactElement | null {
    return <>
      <StyledBoxColumn style={{gap: PD_XLG2, padding: PD_MD, marginTop: -PD_MD}}>
        <StyledBoxColumn style={{border: DIVIDER_BLACK, padding: PD_LG, minHeight: 600}}>
          {this.renderUpload()}
          <StyledSpan/>
          <Button
            disabled={!this.state.draft}
            startIcon={<ArrowForwardIosOutlined/>}
            variant="contained"
            onClick={() => {
              this.saveUpload();
            }}>
            Next
          </Button>
        </StyledBoxColumn>
      </StyledBoxColumn>
    </>;
  }
}